import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import {
  fetchCompany,
  updateCompany,
  createBranch,
  updateBranch,
  deleteBranch,
} from '../../../../services/Configuration/CompanyService';
import SettingCard from '../../../../components/Configuration/SettingCard';
import BranchCard from '../../../../components/Configuration/BranchCard';
import BranchCreateEditModal from './BranchCreateEditModal';
import BranchDeleteModal from './BranchDeleteModal';
import { companyI18n } from './i18n';
import '../configuration.scss';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { useCurrency } from 'context/CurrencyContext';
import { useSnackbar } from 'context/SnackbarContext';
import { getCurrencyByCode } from 'helpers/currencyHelper';
import CurrenciesModal from './CurrenciesModal';

export default function Company() {
  const { language } = useLanguage();
  const companyLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: companyI18n });
  const { DesignSystemComponents } = useOutletContext();
  const { Card, Button, Modal, Input, Dropdown, SvgIcon } =
    DesignSystemComponents ?? {};

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [regionalEditMode, setRegionalEditMode] = useState(false);
  const [regionalDraft, setRegionalDraft] = useState(null);
  const [showEditBranch, setShowEditBranch] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showDeleteBranch, setShowDeleteBranch] = useState(false);
  const [showCurrenciesModal, setShowCurrenciesModal] = useState(false);

  useEffect(() => {
    companyI18n.changeLanguage(companyLanguage);
  }, [companyLanguage]);

  const { currency: contextCurrency, setCurrency } = useCurrency();
  const { showSnackbar } = useSnackbar();

  const {
    canEditRegionalConfig,
    canCreateBranch,
    canEditBranch,
    canDeleteBranch,
  } = usePermissionsConfiguration();

  const loadCompany = async () => {
    const data = await fetchCompany();
    setCompany(data);
    if (data.regionalConfig?.currency?.iso) {
      setCurrency(data.regionalConfig.currency.iso);
    }
  };

  // Carga inicial desde backend
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErrorKey(null);

        const data = await fetchCompany();
        if (mounted) {
          setCompany(data);
          if (data.regionalConfig?.currency?.iso) {
            setCurrency(data.regionalConfig.currency.iso);
          }
        }
      } catch (err) {
        if (mounted) {
          setErrorKey('company_loadError');
          console.error('Error loading company:', err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => (mounted = false);
  }, []);

  const currentTime = useMemo(() => {
    try {
      const tz = company?.regionalConfig?.timezone?.label || 'America/Santiago';
      return new Intl.DateTimeFormat(companyLanguage, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: tz,
      }).format(new Date());
    } catch {
      return '';
    }
  }, [company, companyLanguage]);

  const displayCurrency = company?.regionalConfig?.currency?.name
    ? company.regionalConfig.currency
    : (getCurrencyByCode(contextCurrency) ?? { name: '', symbol: '' });

  const contextCurrencyId = company?._parametricData?.currencies?.find(
    (c) => c.iso === contextCurrency
  )?.id;

  const onEditRegional = () => {
    setRegionalDraft(
      company?.regionalConfig
        ? JSON.parse(JSON.stringify(company.regionalConfig))
        : {}
    );
    setRegionalEditMode(true);
  };

  const onCancelRegional = () => {
    setRegionalEditMode(false);
    setRegionalDraft(null);
  };

  const onSaveRegional = async () => {
    try {
      if (!company?.id) {
        showSnackbar(t('company_alert_noCompanyData'), 'error');
        return;
      }

      const updatedCompany = {
        ...company,
        regionalConfig: regionalDraft,
        _originalIds: {
          currencyId:
            company._parametricData?.currencies?.find(
              (c) => c.name === regionalDraft.currency.name
            )?.id || company._originalIds.currencyId,
          timeZoneSettingId:
            company._parametricData?.timeZones?.find(
              (zone) => zone.name === regionalDraft.timezone.label
            )?.id || company._originalIds.timeZoneSettingId,
          languageId:
            company._parametricData?.languages?.find(
              (lang) => lang.name === regionalDraft.language
            )?.id || company._originalIds.languageId,
          decimalSeparatorId:
            company._parametricData?.decimalSeparators?.find(
              (decimal) => decimal.symbol === regionalDraft.numberFormat.decimal
            )?.id || company._originalIds.decimalSeparatorId,
          thousandSeparatorId:
            company._parametricData?.thousandSeparators?.find(
              (thousands) =>
                thousands.symbol === regionalDraft.numberFormat.thousands
            )?.id || company._originalIds.thousandSeparatorId,
        },
      };

      await updateCompany(updatedCompany);
      setCompany(updatedCompany);
      if (updatedCompany.regionalConfig?.currency?.iso) {
        setCurrency(updatedCompany.regionalConfig.currency.iso);
      }
      setRegionalEditMode(false);
      showSnackbar(t('company_alert_regionalSaved'), 'success');
    } catch (saveError) {
      console.error('Error al guardar configuracion:', saveError);
      showSnackbar(t('company_alert_regionalSaveError'), 'error');
    }
  };

  const onCreateBranch = () => setShowCreateBranch(true);
  const onCloseCreate = () => setShowCreateBranch(false);

  const onEditBranch = (branch) => {
    setSelectedBranch(branch);
    setShowEditBranch(true);
  };

  const onSaveCreate = async (payload) => {
    try {
      if (!company?.id) {
        showSnackbar(t('company_alert_noCompanyId'), 'error');
        return;
      }

      await createBranch(company.id, payload);
      const updatedCompany = await fetchCompany(company.id);
      setCompany(updatedCompany);
      setShowCreateBranch(false);
      showSnackbar(t('company_alert_branchCreated'), 'success');
    } catch (createError) {
      console.error('Error al crear sucursal:', createError);
      showSnackbar(
        `${t('company_alert_branchCreateErrorPrefix')} ${
          createError.response?.data?.message || createError.message
        }`,
        'error'
      );
    }
  };

  const onDeleteBranch = (branch) => {
    setSelectedBranch(branch);
    setShowDeleteBranch(true);
  };

  const onConfirmDelete = async () => {
    try {
      if (!selectedBranch?.id) {
        showSnackbar(t('company_alert_noBranchId'), 'error');
        return;
      }

      await deleteBranch(selectedBranch.id);

      setCompany((prev) => ({
        ...prev,
        branches: (prev?.branches || []).filter(
          (branch) => branch.id !== selectedBranch.id
        ),
      }));

      setShowDeleteBranch(false);
      setSelectedBranch(null);
      showSnackbar(t('company_alert_branchDeleted'), 'success');
    } catch (deleteError) {
      console.error('Error al eliminar sucursal:', deleteError);
      showSnackbar(
        `${t('company_alert_branchDeleteErrorPrefix')} ${
          deleteError.response?.data?.message || deleteError.message
        }`,
        'error'
      );
      setShowDeleteBranch(false);
    }
  };

  if (loading) {
    return (
      <div className='configuration-page gap-4'>
        <p>{t('company_loading')}</p>
      </div>
    );
  }

  if (errorKey) {
    return (
      <div className='configuration-page gap-4'>
        <p>{t(errorKey)}</p>
      </div>
    );
  }

  if (!company || !company.id) {
    return (
      <div className='configuration-page gap-4'>
        <p>{t('company_notFound')}</p>
      </div>
    );
  }

  const { regionalConfig, branches } = company;
  const numberFmt = regionalEditMode
    ? regionalDraft?.numberFormat
    : regionalConfig?.numberFormat;

  return (
    <div className='configuration-page gap-4'>
      <div className='row mb-3'>
        <div className='col-12 mb-3'>
          <h2 className='mb-2'>{t('company_pageTitle')}</h2>
          <p>{t('company_pageSubtitle')}</p>
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-12'>
          <Card>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='d-flex gap-1 flex-column'>
                <h2 className='fw-semibold'>
                  {company.name || t('company_defaultName')}
                </h2>
                <h3>{t('company_regionalTitle')}</h3>
                <p className='caption font-light'>
                  {t('company_regionalSubtitle')}
                </p>
              </div>
              {!regionalEditMode && (
                <div className='d-flex gap-2'>
                  <Button
                    variant='secondary'
                    iconStart='dollarSign'
                    label={t('company_currencies')}
                    size='sm'
                    onClick={() => setShowCurrenciesModal(true)}
                  />
                  <Button
                    variant='primary'
                    iconStart='pencil'
                    label={t('company_editButton')}
                    size='sm'
                    onClick={onEditRegional}
                    disabled={!canEditRegionalConfig}
                  />
                </div>
              )}
            </div>

            <div className='row'>
              <div className='col-12 col-lg-6 mb-4'>
                <SettingCard
                  Card={Card}
                  SvgIcon={SvgIcon}
                  iconName='dollarSign'
                  title={t('company_currencyTitle')}
                  editMode={regionalEditMode}
                  Dropdown={Dropdown}
                  selectOptions={(
                    company?._parametricData?.currencies || []
                  ).map((currency) => ({
                    label: `${currency.name} (${currency.symbol})`,
                    value: currency.id,
                  }))}
                  selectValue={
                    regionalEditMode
                      ? (company._parametricData?.currencies?.find(
                          (c) => c.name === regionalDraft?.currency?.name
                        )?.id ??
                        company._originalIds?.currencyId ??
                        contextCurrencyId)
                      : (company._originalIds?.currencyId ?? contextCurrencyId)
                  }
                  onSelectChange={(val) => {
                    const found = company._parametricData?.currencies?.find(
                      (currency) => currency.id === val
                    );
                    if (found) {
                      setRegionalDraft((draft) => ({
                        ...(draft || {}),
                        currency: {
                          name: found.name,
                          symbol: found.symbol,
                          iso: found.iso,
                        },
                      }));
                    }
                  }}
                >
                  <p className='caption fw-normal'>
                    {(regionalEditMode
                      ? regionalDraft?.currency?.name
                      : displayCurrency.name) || '-'}
                  </p>
                  <p className='mini font-light fw-normal'>
                    {t('company_symbolLabel')}{' '}
                    {(regionalEditMode
                      ? regionalDraft?.currency?.symbol
                      : displayCurrency.symbol) || '-'}
                  </p>
                </SettingCard>
              </div>

              <div className='col-12 col-lg-6 mb-4'>
                <SettingCard
                  Card={Card}
                  SvgIcon={SvgIcon}
                  iconName='clock'
                  title={t('company_timezoneTitle')}
                  editMode={regionalEditMode}
                  Dropdown={Dropdown}
                  selectOptions={(
                    company?._parametricData?.timeZones || []
                  ).map((zone) => ({ label: zone.name, value: zone.id }))}
                  selectValue={
                    regionalEditMode
                      ? (company._parametricData?.timeZones?.find(
                          (z) => z.name === regionalDraft?.timezone?.label
                        )?.id ?? company._originalIds?.timeZoneSettingId)
                      : company._originalIds?.timeZoneSettingId
                  }
                  onSelectChange={(val) => {
                    const found = company._parametricData?.timeZones?.find(
                      (zone) => zone.id === val
                    );
                    if (found) {
                      setRegionalDraft((draft) => ({
                        ...(draft || {}),
                        timezone: {
                          label: found.name,
                          utcOffsets: [found.utcOffset || ''],
                        },
                      }));
                    }
                  }}
                  subtitle={
                    !regionalEditMode && currentTime
                      ? t('company_currentTime', { time: currentTime })
                      : undefined
                  }
                >
                  <p className='caption fw-normal'>
                    {(regionalEditMode
                      ? regionalDraft?.timezone?.label
                      : regionalConfig?.timezone?.label) || '-'}
                  </p>
                </SettingCard>
              </div>

              <div className='col-12 col-lg-6 mb-md-4 mb-lg-0'>
                <SettingCard
                  SvgIcon={SvgIcon}
                  iconName='globe'
                  Card={Card}
                  title={t('company_languageTitle')}
                  editMode={regionalEditMode}
                  Dropdown={Dropdown}
                  selectOptions={(
                    company?._parametricData?.languages || []
                  ).map((lang) => ({ label: lang.name, value: lang.id }))}
                  selectValue={
                    regionalEditMode
                      ? (company._parametricData?.languages?.find(
                          (l) => l.name === regionalDraft?.language
                        )?.id ?? company._originalIds?.languageId)
                      : company._originalIds?.languageId
                  }
                  onSelectChange={(val) => {
                    const found = company._parametricData?.languages?.find(
                      (lang) => lang.id === val
                    );
                    if (found) {
                      setRegionalDraft((draft) => ({
                        ...(draft || {}),
                        language: found.name,
                      }));
                    }
                  }}
                >
                  <p className='caption fw-normal'>
                    {(regionalEditMode
                      ? regionalDraft?.language
                      : regionalConfig?.language) || '-'}
                  </p>
                </SettingCard>
              </div>

              <div className='col-12 col-lg-6'>
                <SettingCard
                  SvgIcon={SvgIcon}
                  iconName='gear'
                  Card={Card}
                  title={t('company_numberFormatTitle')}
                  editMode={regionalEditMode}
                  Dropdown={Dropdown}
                  selectOptions={(
                    company?._parametricData?.decimalSeparators || []
                  ).map((decimal) => ({
                    label: t('company_decimalOption', {
                      symbol: decimal.symbol,
                    }),
                    value: decimal.id,
                  }))}
                  selectValue={
                    regionalEditMode
                      ? (company._parametricData?.decimalSeparators?.find(
                          (d) =>
                            d.symbol === regionalDraft?.numberFormat?.decimal
                        )?.id ?? company._originalIds?.decimalSeparatorId)
                      : company._originalIds?.decimalSeparatorId
                  }
                  onSelectChange={(val) => {
                    const decimal =
                      company._parametricData?.decimalSeparators?.find(
                        (item) => item.id === val
                      );
                    const thousands =
                      company._parametricData?.thousandSeparators?.find(
                        (item) =>
                          item.id === company._originalIds?.thousandSeparatorId
                      );
                    setRegionalDraft((draft) => ({
                      ...(draft || {}),
                      numberFormat: {
                        thousands: thousands?.symbol || '.',
                        decimal: decimal?.symbol || ',',
                        example: `1${thousands?.symbol || '.'}234${
                          decimal?.symbol || ','
                        }56`,
                      },
                    }));
                  }}
                >
                  <p className='caption fw-normal'>
                    {t('company_thousandsLabel')}{' '}
                    <span className='fw-medium'>
                      {numberFmt?.thousands || '-'}
                    </span>
                  </p>
                  <p className='caption fw-normal'>
                    {t('company_decimalsLabel')}{' '}
                    <span className='fw-medium'>
                      {numberFmt?.decimal || '-'}
                    </span>
                  </p>
                </SettingCard>
              </div>
            </div>

            {regionalEditMode && (
              <div className='d-flex gap-2 justify-content-end mt-4'>
                <Button
                  variant='secondary'
                  label={t('company_cancelButton')}
                  size='sm'
                  onClick={onCancelRegional}
                />
                <Button
                  variant='primary'
                  label={t('company_saveButton')}
                  size='sm'
                  onClick={onSaveRegional}
                />
              </div>
            )}
          </Card>
        </div>
      </div>

      <div className='row'>
        <div className='col-12'>
          <Card>
            <div className='d-flex justify-content-between mb-3'>
              <h3 className='mb-0'>{t('company_branchesTitle')}</h3>
              <Button
                variant='primary'
                iconStart='plusLg'
                label={t('company_newBranchButton')}
                size='sm'
                onClick={onCreateBranch}
                disabled={!canCreateBranch}
              />
            </div>

            {!branches || branches.length === 0 ? (
              <p className='caption font-light p-3'>
                {t('company_noBranches')}
              </p>
            ) : (
              <div className='branches-container d-flex flex-column gap-2'>
                {branches.map((branch, idx) => (
                  <BranchCard
                    key={branch.id || idx}
                    DesignSystemComponents={DesignSystemComponents}
                    branch={branch}
                    isEditable={canDeleteBranch}
                    isModifyBranch={canEditBranch}
                    onEdit={onEditBranch}
                    onDelete={onDeleteBranch}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <BranchCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        mode='create'
        show={showCreateBranch}
        onClose={onCloseCreate}
        onSave={onSaveCreate}
      />

      <BranchCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        mode='edit'
        show={showEditBranch}
        initialData={selectedBranch}
        onClose={() => setShowEditBranch(false)}
        onSave={async (updated) => {
          try {
            if (!selectedBranch?.id) {
              showSnackbar(t('company_alert_noBranchId'), 'error');
              return;
            }

            await updateBranch(selectedBranch.id, updated, company?.id);

            setCompany((prev) => ({
              ...prev,
              branches: (prev?.branches || []).map((branch) =>
                branch.id === selectedBranch.id
                  ? { ...branch, ...updated, id: selectedBranch.id }
                  : branch
              ),
            }));

            setShowEditBranch(false);
            showSnackbar(t('company_alert_branchUpdated'), 'success');
          } catch (updateError) {
            console.error('Error al actualizar sucursal:', updateError);
            showSnackbar(
              `${t('company_alert_branchUpdateErrorPrefix')} ${
                updateError.response?.data?.message || updateError.message
              }`,
              'error'
            );
          }
        }}
      />

      <BranchDeleteModal
        Modal={Modal}
        show={showDeleteBranch}
        branch={selectedBranch}
        onClose={() => setShowDeleteBranch(false)}
        onConfirm={onConfirmDelete}
      />

      <CurrenciesModal
        Modal={Modal}
        Input={Input}
        Button={Button}
        show={showCurrenciesModal}
        onClose={() => setShowCurrenciesModal(false)}
        onRefresh={loadCompany}
      />
    </div>
  );
}
